import { cache } from './cache';
import { getApiUrl } from '../helper/getApiUrl';

import type { InputOptions, Place, PlaceDetails } from './maps.types';

const apiUrl = getApiUrl();

const getApiKey = async () => {
    const response = await fetch(`${apiUrl}/maps/key`);

    if (response.status !== 200) {
        return undefined;
    }

    const json = await response.json();

    return json.data.apiKey;
};

const search =
    (includedPrimaryTypes: string[]) =>
    async (input: string, { apiKey, language, sessionToken }: InputOptions): Promise<Place[]> => {
        try {
            const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': apiKey,
                },
                body: JSON.stringify({
                    input,
                    sessionToken,
                    includedPrimaryTypes,
                    languageCode: language,
                }),
            });

            if (response.status !== 200) {
                throw new Error('Failed to search for city');
            }

            const json = await response.json();

            return json.suggestions.map(({ placePrediction }: any) => ({
                place: placePrediction.place,
                placeId: placePrediction.placeId,
                mainText: placePrediction.structuredFormat?.mainText?.text,
                secondaryText: placePrediction.structuredFormat?.secondaryText?.text,
            }));
        } catch (err) {
            console.warn('Error while using Google Maps Autocomplete API', {
                details: {
                    input,
                    includedPrimaryTypes,
                },
                error: err,
            });

            return [];
        }
    };

const getPlaceDetails = async (
    placeId: string,
    { apiKey, language, sessionToken }: InputOptions,
): Promise<PlaceDetails | undefined> => {
    try {
        const response = await fetch(
            `https://places.googleapis.com/v1/places/${placeId}?languageCode=${language}&sessionToken=${sessionToken}`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-FieldMask': 'addressComponents',
                    'X-Goog-Api-Key': apiKey,
                    // TODO Pass the session key
                },
            },
        );

        if (response.status !== 200) {
            throw new Error(`Failed to fetch place by id ${placeId}`);
        }

        const json = await response.json();

        const get = (type: string) =>
            json.addressComponents.find((component: { types: string[] }) =>
                component.types.includes(type),
            );

        const country = get('country')?.longText;
        const city = get('locality')?.longText || get('postal_town')?.longText;
        const street = get('route')?.longText;
        const streetNumber = get('street_number')?.longText;
        const postal = get('postal_code')?.longText;

        return {
            country,
            city,
            street,
            streetNumber,
            postal,
        };
    } catch (err) {
        console.warn('Error while using Google Maps Details API', {
            details: {
                placeId,
            },
            error: err,
        });

        return undefined;
    }
};

export const mapsApi = {
    getApiKey,
    fullAddress: {
        search: cache(search(['route'])),
    },
    places: {
        getDetails: getPlaceDetails,
    },
    cities: {
        search: cache(search(['locality', 'postal_town'])),
    },
    streets: {
        search: cache(search(['route'])),
    },
    postals: {
        search: cache(search(['postal_code'])),
    },
};
